import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';

const CreateFolderModal = ({ open, onClose, onCreate }) => {
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (open) {
      setFolderName('');
      setError('');
      setCreating(false);
    }
  }, [open]);

  const validateFolderName = (name) => {
    if (!name.trim()) {
      return 'Folder name is required';
    }
    if (name.includes('/') || name.includes('\\')) {
      return 'Folder name cannot contain slashes';
    }
    if (name.includes('..')) {
      return 'Invalid folder name';
    }
    if (name.length > 255) {
      return 'Folder name is too long';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateFolderName(folderName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setCreating(true);
    try {
      await onCreate(folderName.trim());
    } catch {
      setError('Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const handleChange = (e) => {
    setFolderName(e.target.value);
    if (error) setError('');
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <CreateNewFolderOutlinedIcon />
          <Typography level="h4">Create New Folder</Typography>
        </Stack>
        <Typography sx={{ mb: 2 }}>Enter a name for the new folder.</Typography>

        <form id="create-folder-form" onSubmit={handleSubmit}>
          <FormControl error={!!error}>
            <FormLabel>Folder Name</FormLabel>
            <Input
              placeholder="Enter folder name"
              value={folderName}
              onChange={handleChange}
              autoFocus
            />
            {error && (
              <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </FormControl>
        </form>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'neutral.outlinedBorder', position: 'sticky', bottom: 0, bgcolor: 'background.surface' }}>
        <Button
          type="submit"
          form="create-folder-form"
          color="primary"
          loading={creating}
          disabled={!folderName.trim()}
          sx={{ width: '100%' }}
        >
          Create
        </Button>
      </Box>
    </Drawer>
  );
};

export default CreateFolderModal;
